import { useEventListener, useDocumentVisibility, useIntervalFn, useLocalStorage } from '@vueuse/core'
import { watch } from 'vue'
import useAuthStore from '@/stores/authStore'
import { logoutReason } from '@/composables/useAuth'

// PERMANENT composable (not legacy): 30-minute inactivity logout, kept from the
// old App.vue and rewritten for the new structure.
// It only clears tokens — the cache flush + redirect to /authenticate is owned
// by useAuthEffects reacting to selectedToken becoming null.

const IDLE_TIMEOUT_MS = 30 * 60_000
const ACTIVITY_THROTTLE_MS = 10_000
const IDLE_CHECK_INTERVAL_MS = 60_000
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'click'] as const

export function useIdleTimeout() {
  const authStore = useAuthStore()
  // Cross-tab shared timestamp — replaces the old PersistedStateClient
  // broadcast of Vuex last_activity_at.
  const lastActivityAt = useLocalStorage<number | null>('last_activity_at', null)

  const isAuthed = () => authStore.selectedToken != null

  function markActivity() {
    if (!isAuthed()) return
    // Throttled so we don't spam storage on every event.
    const now = Date.now()
    if (lastActivityAt.value != null && now - lastActivityAt.value < ACTIVITY_THROTTLE_MS) return
    lastActivityAt.value = now
  }

  function hasBeenIdleTooLong() {
    return lastActivityAt.value != null && Date.now() - lastActivityAt.value >= IDLE_TIMEOUT_MS
  }

  function logoutIfIdle() {
    if (isAuthed() && hasBeenIdleTooLong()) {
      logoutReason.value = 'inactivity'
      authStore.clearAllTokens()
    }
  }

  for (const evt of ACTIVITY_EVENTS) {
    useEventListener(window, evt, markActivity, { passive: true })
  }

  const visibility = useDocumentVisibility()
  watch(visibility, (v) => {
    if (v !== 'visible') return
    if (isAuthed() && hasBeenIdleTooLong()) {
      logoutIfIdle()
    } else {
      markActivity()
    }
  })

  useIntervalFn(logoutIfIdle, IDLE_CHECK_INTERVAL_MS)

  // A fresh login must not inherit a stale timestamp (persisted cross-tab in
  // localStorage) — an in-SPA BankID login would otherwise be idle-logged-out
  // by the next interval tick.
  watch(isAuthed, (authed) => {
    if (authed) lastActivityAt.value = Date.now()
  })

  if (isAuthed()) markActivity()
}
